import React, { useCallback, forwardRef } from 'react'
import { useIntl } from 'react-intl'

import TourV2DurationText from './TourV2DurationText'
import BookmarkCard from '../BookmarkCardNew/BookmarkCard'
import BookmarkLocation from '../BookmarkCardNew/BookmarkLocation'

import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import BodyText from 'LuxKit/Typography/BodyText'
import { getDepositPropertiesForTourV2 } from 'components/OfferList/OfferListTiles/YouMayAlsoLikeTile/Common/createYouMayAlsoLikeTileView'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { max } from 'lib/array/arrayUtils'
import type { ResponsiveImageOptions } from 'lib/image/imageUtils'
import { getImageUrl } from 'lib/image/imageUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import TourV2OfferCardPriceDetails from './TourV2OfferCardPriceDetails'

interface Props {
  currency: string
  purchasableOption: App.Tours.TourV2OfferPurchasableOption
  variation: App.Tours.TourV2OfferVariation
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
  linkQuery?: URLSearchParams
  onClick?: (offer: App.Tours.TourV2OfferSummary | App.Tours.TourV2Offer) => void
  aspectRatio?: string
  source?: string
  className?: string
  target?: '_blank'
}

export function getItineraryDaysCount(variation: App.Tours.TourV2OfferVariation): number | null {
  if (!variation.itinerary) return null
  const lastDay = max(variation.itinerary, (item) => item.startDay)
  if (!lastDay) return null
  const daysCount = lastDay.startDay + lastDay.duration - 1
  return daysCount
}

const IMAGE_PARAMS: ResponsiveImageOptions = {
  width: '400px',
}

const TourV2CardContent = forwardRef<HTMLAnchorElement, Props>(
  function TourV2CardContent(props: Props, ref) {
    const {
      purchasableOption,
      variation,
      offer,
      currency,
      linkQuery,
      onClick,
      aspectRatio,
      source,
      className,
      target,
    } = props

    const intl = useIntl()
    const {
      startLocation,
      endLocation,
      hasTheSameStartAndEndLocation,
      countriesCount,
      durationInDaysCount,
      placesCount,
    } = useTourV2VariationDetails(variation)

    const image = variation.images[0]
    const imageUrl =
      image?.url ??
      (image.id &&
        getImageUrl(
          image.id,
          aspectRatio ? { aspectRatio, ...IMAGE_PARAMS } : IMAGE_PARAMS,
        ))
    const duration = getItineraryDaysCount(variation)

    const depositProps = getDepositPropertiesForTourV2(offer, currency)

    const depositComponent = depositProps.depositShouldDisplay ? (
      <SecureWithDepositTagAndModal
        depositAmountPercentage={depositProps.depositAmountPercentage}
        depositAmount={depositProps.depositAmount}
        depositType={depositProps.depositType}
        offerType={offer.type}
        depositThresholds={offer.depositThresholds}
      />
    ) : null

    const onTileClick = useCallback(() => {
      onClick?.(offer)
    }, [offer, onClick])

    const offerUrl = addQueryParamsToPath(offerPageURL(offer, linkQuery), {
      source,
    })

    return (
      <BookmarkCard
        ref={ref}
        className={className}
        title={offer.name}
        body=""
        additionalContent={
          <>
            <BodyText variant="small" weight="normal">
              {intl.formatMessage(
                {
                  id: '3UdQDc7eDy',
                  defaultMessage: 'Operated by {name}',
                  description: 'Form field label for the tour operator on tour bookmark card',
                },
                { name: offer.brand.name },
              )}
            </BodyText>
            {!!durationInDaysCount && <TourV2DurationText
              durationInDaysCount={durationInDaysCount}
              placesCount={placesCount}
              countriesCount={countriesCount}
            />}
          </>
        }
        bookmarkButton={undefined}
        location={
          <BookmarkLocation
            startLocation={startLocation}
            endLocation={
              hasTheSameStartAndEndLocation ? startLocation : endLocation
            }
            variant="start_and_end"
          />
        }
        imageUrl={imageUrl}
        productType={offer.productType}
        offerPriceDetails={
          <TourV2OfferCardPriceDetails
            purchasableOption={purchasableOption}
            duration={duration}
            depositComponent={depositComponent}
            currencyCode={currency}
          />
        }
        offerUrl={offerUrl}
        onClick={onTileClick}
        urgencyLabels={[]} // TODO fix me
        isSoldOut={false} // TODO fix me
        target={target}
      />
    )
  },
)

export default TourV2CardContent
